import { useMemo } from "react"
import { toast } from "sonner"
import { useNavigate } from "react-router"
import { useQueryClient } from "@tanstack/react-query"

import { TaskFrame, type TaskIO } from "./task-frame"
import { useAnswerTask, useInvalidateTaskWorld, useSkipTask, useSnoozeEstimate, useEstimateTasks } from "@/lib/api/use-tasks"
import { useUpdateEstimateCustomer, useEstimate } from "@/lib/api/use-estimates"
import { useCustomerActivity, useSetCustomerConsent } from "@/lib/api/use-customer-activity"
import { appointmentsApi } from "@/lib/api/appointments"
import { estimatesApi } from "@/lib/api/estimates"
import { tasksApi, type FollowUpCadence, type TaskRow } from "@/lib/api/tasks"
import { queryKeys } from "@/lib/api/query-keys"
import { useAuth } from "@/lib/auth/auth-context"
import type { TaskResult } from "@/lib/tasks/task-types"

// The data shell around the frame: everything one task needs, fetched once,
// and every write it can make, behind one `TaskIO`. No toasts here except the
// failure ones — the page owns the one confirmation line.
//
// Writes that happen MID-flow (a visit outcome before "booked the drop-off?")
// don't invalidate on their own: a refetch would drop the derived row and
// unmount the card under her. Every flow ends in onDone, and THAT invalidates.

function useTaskIO(row: TaskRow): TaskIO {
  const answer = useAnswerTask()
  const skip = useSkipTask()
  const snooze = useSnoozeEstimate()
  const contact = useUpdateEstimateCustomer()
  const consent = useSetCustomerConsent(row.customerId)
  const navigate = useNavigate()

  const busy = answer.isPending || skip.isPending || snooze.isPending || contact.isPending || consent.isPending

  return useMemo<TaskIO>(
    () => ({
      busy,
      post: async (a, note) => {
        try {
          return await answer.mutateAsync({ taskId: row.id, estimateId: row.estimateId, answer: a, note })
        } catch (err) {
          const status = (err as { status?: number })?.status
          toast.error(status === 409 ? "That task was already handled." : "Couldn't save that — try once more.")
          throw err
        }
      },
      postTo: async (taskId, a, note) => {
        try {
          return await answer.mutateAsync({ taskId, estimateId: row.estimateId, answer: a, note })
        } catch (err) {
          toast.error("Couldn't save that — try once more.")
          throw err
        }
      },
      skip: async () => {
        try {
          const res = await skip.mutateAsync({ taskId: row.id, estimateId: row.estimateId })
          return res.task
        } catch (err) {
          toast.error("Couldn't move that.")
          throw err
        }
      },
      snooze: async (until, note) => {
        try {
          await snooze.mutateAsync({ estimateId: row.estimateId, action: "snooze", until, note: note ?? undefined })
        } catch (err) {
          toast.error("Couldn't move that.")
          throw err
        }
      },
      setVisitOutcome: async (appointmentId, outcome) => {
        try {
          await appointmentsApi.setOutcome(appointmentId, outcome)
        } catch (err) {
          toast.error("Couldn't record the visit.")
          throw err
        }
      },
      savePhone: async (estimateId, phone) => {
        try {
          await contact.mutateAsync({ estimateId, phone })
        } catch (err) {
          toast.error("Couldn't save the number.")
          throw err
        }
      },
      stopTexts: async () => {
        try {
          await consent.mutateAsync({ state: "handled_offline", reason: "Handling it by phone" })
        } catch (err) {
          toast.error("Couldn't stop the texts.")
          throw err
        }
      },
      wake: async (estimateId) => {
        await tasksApi.snooze(estimateId, "wake")
      },
      followUpCall: async (estimateId, inDays) => {
        try {
          await tasksApi.followUpCall(estimateId, inDays)
        } catch (err) {
          toast.error("Couldn't put the call on your list.")
          throw err
        }
      },
      decline: async (estimateId, reason, note) => {
        try {
          await tasksApi.decline(estimateId, reason, note)
        } catch (err) {
          toast.error("Couldn't close that.")
          throw err
        }
      },
      openEdit: (estimateId) => navigate(`/estimates/${estimateId}/edit`),
      startFollowUp: async (estimateId) => {
        try {
          await estimatesApi.updateStatus(estimateId, { status: "InProgress" })
        } catch (err) {
          toast.error("Confirmed, but couldn't start the follow-up — move it from the board.")
          throw err
        }
      },
    }),
    [busy, answer, skip, snooze, contact, consent, navigate, row.id, row.estimateId],
  )
}

interface TaskWorkspaceProps {
  row: TaskRow
  teamId: number
  cadence: FollowUpCadence | null
  onDone: (result: TaskResult) => void
  onSwitchToCall?: () => void
}

export function TaskWorkspace({ row, teamId, cadence, onDone, onSwitchToCall }: TaskWorkspaceProps) {
  const { user } = useAuth()
  const meId = user?.id != null ? Number(user.id) : null
  const { data: estimate } = useEstimate(row.estimateId)
  const activity = useCustomerActivity(row.customerId ?? null)
  const history = useEstimateTasks(row.estimateId)
  const io = useTaskIO(row)
  const qc = useQueryClient()
  const invalidate = useInvalidateTaskWorld()

  const done = (result: TaskResult) => {
    invalidate(row.estimateId)
    qc.invalidateQueries({ queryKey: queryKeys.appointments.all })
    onDone(result)
  }

  // Below xl the card and the rail stack; at xl `contents` hands both to the
  // page's grid as siblings, so the rail becomes the third column.
  return (
    <div className="min-w-0 xl:contents">
      <TaskFrame
        row={row}
        teamId={teamId}
        cadence={cadence}
        io={io}
        data={{ estimate, activity: activity.data, history: history.data?.tasks, meId }}
        onDone={done}
        onSwitchToCall={onSwitchToCall}
      />
    </div>
  )
}
